// Predicates over `OpportunityDocument.permissions`, i.e. what the PDF's own security settings allow.
// `permissions` is null for non-PDF documents and for documents uploaded before permission capture
// existed, so null/undefined means "everything allowed" (backward compatibility).

const DOCUMENT_PERMISSION_FLAGS = Object.freeze([
  'copyAllowed',
  'formFillAllowed',
  'annotationAllowed',
  'textExtractionAllowed',
]);

const getDocumentPermissions = (document) => (document && document.permissions) || null;

const hasDocumentPermissions = (document) => getDocumentPermissions(document) !== null;

// Only an explicit `false` restricts; a missing flag is treated as allowed.
const isDocumentPermissionAllowed = (document, flag) => {
  const permissions = getDocumentPermissions(document);
  return !permissions || permissions[flag] !== false;
};

const canCopyDocument = (document) => isDocumentPermissionAllowed(document, 'copyAllowed');
const canAnnotateDocument = (document) => isDocumentPermissionAllowed(document, 'annotationAllowed');
const canFillDocumentForms = (document) => isDocumentPermissionAllowed(document, 'formFillAllowed');
const canExtractDocumentText = (document) => isDocumentPermissionAllowed(document, 'textExtractionAllowed');

// Informational only: an owner password never restricts the user by itself.
const isDocumentOwnerPasswordProtected = (document) =>
  Boolean(getDocumentPermissions(document)?.ownerPasswordProtected);

// One row per `*Allowed` flag, in display order.
const getDocumentPermissionRows = (document) =>
  DOCUMENT_PERMISSION_FLAGS.map((key) => ({ key, allowed: isDocumentPermissionAllowed(document, key) }));

const hasRestrictedDocumentPermissions = (document) => getDocumentPermissionRows(document).some((row) => !row.allowed);

export {
  DOCUMENT_PERMISSION_FLAGS,
  canAnnotateDocument,
  canCopyDocument,
  canExtractDocumentText,
  canFillDocumentForms,
  getDocumentPermissionRows,
  hasDocumentPermissions,
  hasRestrictedDocumentPermissions,
  isDocumentOwnerPasswordProtected,
};
